import { isNull, list, sql, todo } from "@cartero/powersync";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CENTRALIZED LIVE LAYER (single choke point)              │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * This module is the ONLY place where the invariant «live row = `deleted_at IS
 * NULL`» is expressed for the canon tables with a trash can (`todo`, `list`).
 * Every live read composes these predicates (the domain builders in
 * todos/lists/theme/live import them from here). The ONLY reads that see deleted
 * rows are the trash (`getTrashed*Query`) and a harmless id-scoped lookup,
 * both declared in the guard whitelist (`guard-manifest.ts`).
 *
 * Why centralize? The scattered invariant was the campaign's #1 bug family
 * (stats/backlinks/PATCH that ignored `deleted_at`). With a single choke point
 * + anti-regression guard, adding a raw read of these tables outside the
 * whitelist BREAKS the build (see `live/__tests__/live-access-guard.test.ts`).
 *
 * Schema NOTE (cartero canon): only `todo` and `list` carry `deleted_at`.
 * `subtask` is NOT soft-delete (it is cascade-deleted when purging its parent
 * `todo`/`list`), which is why it has no live filter. Clones whose child table
 * DOES have a trash can must add their own `liveXFilters()` here and their
 * manifest entry.
 */

/**
 * Predicates for a «live task»: the task itself is not in the trash, nor is its
 * parent list. Applies to EVERY live view/query of `todo`.
 */
export function liveTodoFilters() {
  return [
    isNull(todo.deleted_at),
    sql`NOT EXISTS (SELECT 1 FROM ${list} WHERE ${list.id} = ${todo.list_id} AND ${list.deleted_at} IS NOT NULL)`,
  ];
}

/**
 * Predicates for a «live list»: the list is not in the trash. Applies to
 * EVERY live view/query of `list`.
 */
export function liveListFilters() {
  return [isNull(list.deleted_at)];
}
